import React, { useEffect, useMemo, useState } from 'react';
import Button from '../ui/Button';
import ObservationCommentComposer from '../Observations/shared/ObservationCommentComposer';
import ObservationFileList from '../Observations/shared/ObservationFileList';
import usePendingObservationFiles from '../Observations/shared/usePendingObservationFiles';
import {
  createRequestObservationNote,
  fetchObservationFileCategories,
  fetchRequestObservations,
  replyToRequestObservationNote,
  uploadRequestObservationFile
} from '../../services/comersRequestFlow';

const statusClassMap = {
  open: 'approved-requests-page__observation-badge approved-requests-page__observation-badge--open',
  resolved: 'approved-requests-page__observation-badge approved-requests-page__observation-badge--resolved',
  annulled: 'approved-requests-page__observation-badge approved-requests-page__observation-badge--annulled'
};

const formatThreadTimestamp = (note) => {
  if (note.date && note.time) return `${note.date} · ${note.time}`;
  if (note.date) return note.date;
  if (note.time) return note.time;
  return '';
};

const TechnicalIdCopy = ({ uid }) => {
  const [copied, setCopied] = useState(false);

  if (!uid) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(uid);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <button
      type="button"
      className="approved-requests-page__technical-id"
      onClick={handleCopy}
      title={uid}
    >
      <i className="bi bi-copy" aria-hidden />
      {copied ? 'ID copiado' : 'ID técnico / copiar'}
    </button>
  );
};

const ObservationComposerSection = ({
  observationId,
  requestUid,
  categories,
  draftValue,
  onDraftChange,
  onError,
  onSubmitted
}) => {
  const {
    pendingFiles,
    addFile,
    removeFile,
    toggleCategory,
    clearPendingFiles,
    validatePendingFiles
  } = usePendingObservationFiles({ onError });

  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleSubmit = async () => {
    const content = (draftValue || '').trim();
    if (!content && pendingFiles.length === 0) return;
    if (!validatePendingFiles()) return;

    setSubmitting(true);
    onError('');

    try {
      if (content) {
        await createRequestObservationNote({
          requestUid,
          observationId,
          content
        });
      }

      if (pendingFiles.length > 0) {
        setUploading(true);
        setUploadProgress(0);

        const total = pendingFiles.length;
        for (let index = 0; index < pendingFiles.length; index += 1) {
          const item = pendingFiles[index];
          await uploadRequestObservationFile({
            requestUid,
            observationId,
            file: item.file,
            categories: item.categories,
            onUploadProgress: (event) => {
              if (!event.total) return;
              const fileProgress = event.loaded / event.total;
              setUploadProgress(Math.round(((index + fileProgress) / total) * 100));
            }
          });
        }
      }

      onDraftChange('');
      clearPendingFiles();
      await onSubmitted?.();
    } catch (submitError) {
      onError(submitError.response?.data?.error?.message || 'No se pudo enviar el comentario.');
    } finally {
      setSubmitting(false);
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <ObservationCommentComposer
      value={draftValue}
      onChange={onDraftChange}
      pendingFiles={pendingFiles}
      categories={categories}
      onAddFile={addFile}
      onRemovePendingFile={removeFile}
      onTogglePendingCategory={toggleCategory}
      onSubmit={handleSubmit}
      submitting={submitting}
      uploading={uploading}
      uploadProgress={uploadProgress}
    />
  );
};

const RequestObservationsModal = ({ request, open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const [fileCategories, setFileCategories] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [submittingKey, setSubmittingKey] = useState('');
  const [expandedReplies, setExpandedReplies] = useState({});

  useEffect(() => {
    if (!open || !request?.uid) {
      setPayload(null);
      setFileCategories([]);
      setError('');
      setExpandedReplies({});
      return;
    }

    let ignore = false;

    const load = async () => {
      setLoading(true);
      setError('');

      try {
        const [data, categories] = await Promise.all([
          fetchRequestObservations({ requestUid: request.uid }),
          fetchObservationFileCategories()
        ]);
        if (!ignore) {
          setPayload(data);
          setFileCategories(categories);
        }
      } catch (loadError) {
        if (!ignore) {
          setPayload(null);
          setError(loadError.response?.data?.error?.message || 'No se pudieron cargar las observaciones.');
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      ignore = true;
    };
  }, [open, request?.uid]);

  const observations = useMemo(() => payload?.observations || [], [payload]);

  if (!open) {
    return null;
  }

  const setDraftValue = (key, value) => {
    setDrafts((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const toggleReply = (noteId) => {
    setExpandedReplies((prev) => ({
      ...prev,
      [noteId]: !prev[noteId]
    }));
  };

  const submitReply = async (observationId, noteId) => {
    const key = `reply-${noteId}`;
    const content = (drafts[key] || '').trim();
    if (!content) return;

    setSubmittingKey(key);
    try {
      await replyToRequestObservationNote({
        requestUid: request.uid,
        observationId,
        noteId,
        content
      });

      setDraftValue(key, '');
      setExpandedReplies((prev) => ({ ...prev, [noteId]: false }));
      const data = await fetchRequestObservations({ requestUid: request.uid });
      setPayload(data);
    } catch (submitError) {
      setError(submitError.response?.data?.error?.message || 'No se pudo enviar la respuesta.');
    } finally {
      setSubmittingKey('');
    }
  };

  const refreshObservations = async () => {
    const data = await fetchRequestObservations({ requestUid: request.uid });
    setPayload(data);
  };

  return (
    <div className="approved-requests-page__modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="approved-requests-page__modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="request-observations-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="approved-requests-page__modal-header">
          <div>
            <h2 id="request-observations-modal-title" className="approved-requests-page__modal-title">
              Observaciones de la solicitud
            </h2>
            <p className="approved-requests-page__modal-subtitle">
              {request.customer}
              {request.id != null ? ` · #${request.id}` : ''}
            </p>
            <TechnicalIdCopy uid={request.uid} />
          </div>
          <button type="button" className="approved-requests-page__modal-close" onClick={onClose} aria-label="Cerrar">
            <i className="bi bi-x-lg" aria-hidden />
          </button>
        </div>

        <div className="approved-requests-page__modal-content">
          <div className="approved-requests-page__modal-body">
            {loading && (
              <div className="approved-requests-page__modal-empty">
                Cargando observaciones...
              </div>
            )}

            {!loading && error && (
              <div className="approved-requests-page__modal-error">
                {error}
              </div>
            )}

            {!loading && !error && observations.length === 0 && (
              <div className="approved-requests-page__modal-empty">
                Esta solicitud no tiene observaciones registradas.
              </div>
            )}

            {!loading && !error && observations.map((observation) => (
              <section key={observation.id} className="approved-requests-page__observation-block">
                <div className="approved-requests-page__observation-badges">
                  <span className={statusClassMap[observation.status] || statusClassMap.annulled}>
                    {observation.status_label || observation.status}
                  </span>
                  {observation.status === 'open' && observation.action_owner_label && (
                    <span className="approved-requests-page__observation-action-badge">
                      {observation.action_owner_label}
                    </span>
                  )}
                </div>

                <div className="approved-requests-page__observation-source-meta">
                  {observation.created_by || 'Soporte Cirenio'}
                  {' · '}
                  {observation.created_at_label || 'Sin fecha'}
                </div>

                <div className="approved-requests-page__observation-initial">
                  <div className="approved-requests-page__observation-initial-label">Observación inicial</div>
                  <p className="approved-requests-page__observation-initial-text">
                    {observation.description}
                  </p>
                </div>

                {(observation.notes || []).length > 0 && (
                  <div className="approved-requests-page__observation-section approved-requests-page__observation-section--comments">
                    <h3 className="approved-requests-page__observation-section-title">Comentarios</h3>
                    <div className="approved-requests-page__thread-list">
                      {(observation.notes || []).map((note) => (
                        <article key={note.id} className="approved-requests-page__thread-item">
                          <div className="approved-requests-page__thread-head">
                            <span className="approved-requests-page__thread-author">
                              {note.user?.name || 'Usuario'}
                            </span>
                            <span className="approved-requests-page__thread-date">
                              {formatThreadTimestamp(note)}
                            </span>
                          </div>
                          <p className="approved-requests-page__thread-content">{note.content}</p>

                          {(note.replies || []).length > 0 && (
                            <div className="approved-requests-page__reply-list">
                              {note.replies.map((reply) => (
                                <div key={reply.id} className="approved-requests-page__reply-item">
                                  <div className="approved-requests-page__thread-head">
                                    <span className="approved-requests-page__thread-author">
                                      {reply.user?.name || 'Usuario'}
                                    </span>
                                    <span className="approved-requests-page__thread-date">
                                      {formatThreadTimestamp(reply)}
                                    </span>
                                  </div>
                                  <p className="approved-requests-page__thread-content">{reply.content}</p>
                                </div>
                              ))}
                            </div>
                          )}

                          {observation.can_reply && (
                            <div className="approved-requests-page__reply-toggle">
                              {!expandedReplies[note.id] ? (
                                <Button
                                  type="button"
                                  variant="link"
                                  size="sm"
                                  className="approved-requests-page__reply-link"
                                  onClick={() => toggleReply(note.id)}
                                >
                                  Responder
                                </Button>
                              ) : (
                                <div className="approved-requests-page__reply-form">
                                  <textarea
                                    className="form-control approved-requests-page__reply-input"
                                    rows="2"
                                    maxLength="1000"
                                    placeholder="Responder a este comentario"
                                    value={drafts[`reply-${note.id}`] || ''}
                                    onChange={(event) => setDraftValue(`reply-${note.id}`, event.target.value)}
                                  />
                                  <div className="approved-requests-page__reply-actions">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => toggleReply(note.id)}
                                      disabled={submittingKey === `reply-${note.id}`}
                                    >
                                      Cancelar
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="outlinePrimary"
                                      size="sm"
                                      onClick={() => submitReply(observation.id, note.id)}
                                      disabled={submittingKey === `reply-${note.id}` || !(drafts[`reply-${note.id}`] || '').trim()}
                                    >
                                      Enviar respuesta
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </article>
                      ))}
                    </div>
                  </div>
                )}

                {(observation.files || []).length > 0 && (
                  <div className="approved-requests-page__observation-section approved-requests-page__observation-section--files">
                    <h3 className="approved-requests-page__observation-section-title">Archivos adjuntos</h3>
                    <ObservationFileList files={observation.files} compact />
                  </div>
                )}
              </section>
            ))}
          </div>

          {!loading && !error && observations.some((observation) => observation.can_reply) && (
            <div className="approved-requests-page__modal-composer">
              {observations
                .filter((observation) => observation.can_reply)
                .map((observation) => (
                  <ObservationComposerSection
                    key={observation.id}
                    observationId={observation.id}
                    requestUid={request.uid}
                    categories={fileCategories}
                    draftValue={drafts[`observation-${observation.id}`] || ''}
                    onDraftChange={(value) => setDraftValue(`observation-${observation.id}`, value)}
                    onError={setError}
                    onSubmitted={refreshObservations}
                  />
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequestObservationsModal;
